<!-- `role="radiogroup"` on the fieldset: a bare <fieldset> announces as a plain group, which does not carry
     "1 von 2". The <legend> still supplies the accessible name, so nothing needs aria-label here. -->
<fieldset role="radiogroup">
  <legend class="block text-sm font-medium leading-6 text-gray-900">{{ legend() }}</legend>

  <!-- The track, dressed as a field.
       `h-9` is the rendered height of the form's own text inputs (`p-1.5` + `sm:text-sm sm:leading-6`
       = 12 + 24 px); it is an exact number rather than a min-height because the segments size themselves off
       it with `h-full`, which is what makes the pill inset by the padding alone.
       `bg-white` + a grey edge, because this control's home is a `bg-gray-100` form panel, where a grey track
       is invisible and the unselected half stops reading as pressable.
       `border-[1px]`, never `border`: Bootstrap's is a shorthand with `!important` that also nails the colour
       to its own grey. Same story for `rounded`, hence `rounded-md`. -->
  <div class="mt-2 flex h-9 w-full gap-0.5 rounded-md border-[1px] border-solid border-gray-300 bg-white p-0.5
              shadow-sm">
    @for (option of options(); track option.value; let i = $index) {
      <!-- `rounded-[3px]`: the pill sits 3 px inside the track (1 px border + 2 px padding), so an inner radius
           of 6 − 3 = 3 px is what keeps its corners concentric with the track's `rounded-md`. Tailwind's
           `rounded-sm` is 2 px and `rounded` is Bootstrap's name, so the arbitrary value is the honest one.

           `outline`, not `ring`, for focus: the track's gutter is 2 px, so an offset ring would be clipped by
           the border — and an INSET ring on the selected segment is a theme-coloured line drawn on a
           theme-coloured pill, i.e. invisible. An outline is painted outside the pill, over the gutter, and
           reads against both the white track and the filled pill. The input is `sr-only`, so this is the only
           visible focus a keyboard user can get; it is not optional. -->
      <label [for]="segmentId(i)"
             class="relative flex h-full min-w-0 flex-1 select-none items-center justify-center rounded-[3px]
                    px-2 text-center text-sm font-medium transition-colors duration-150
                    focus-within:outline focus-within:outline-2 focus-within:outline-offset-1
                    focus-within:outline-theme-primary-700"
             [class.cursor-pointer]="!option.disabled"
             [class.cursor-not-allowed]="option.disabled"
             [class.bg-theme-primary-700]="isSelected(option) && !option.disabled"
             [class.text-white]="isSelected(option) && !option.disabled"
             [class.shadow-sm]="isSelected(option) && !option.disabled"
             [class.bg-gray-200]="isSelected(option) && option.disabled"
             [class.text-gray-400]="option.disabled"
             [class.text-gray-700]="!isSelected(option) && !option.disabled"
             [class.hover:bg-gray-100]="!isSelected(option) && !option.disabled"
             [attr.data-selected]="isSelected(option)"
             [attr.data-value]="option.value">
        <!-- A real radio, `sr-only` rather than `hidden`: hidden inputs are not focusable, and losing focus
             here means losing the arrow keys and the grouped announcement this control exists for. -->
        <input class="sr-only" type="radio" [id]="segmentId(i)" [name]="name()"
               [checked]="isSelected(option)" [disabled]="!!option.disabled"
               (change)="choose(option)" (keydown.enter)="onEnter($event, option)">
        <!-- `truncate` so a long label cannot push a fixed-height row out of shape on a narrow screen. Visual
             only: the accessible name comes from this same text node, so nothing is lost to a screen reader. -->
        <span class="truncate">{{ option.label }}</span>
      </label>
    }
  </div>
</fieldset>
